import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, BadgeCheck, Lock, MapPin, Package, Store } from "lucide-react";
import { api } from "../lib/api";

const LockScreen = ({ reason }) => (
    <div className="mx-auto max-w-7xl px-4 py-24 text-center sm:px-6" data-testid="suppliers-locked">
        <Lock className="mx-auto mb-4 h-10 w-10 text-terra-500" />
        <p className="font-display text-2xl font-bold">Bu sayfa yalnızca kayıtlı alıcılar içindir</p>
        <p className="mx-auto mt-2 max-w-md text-sm text-stone-400">
            {reason === "role"
                ? "Tedarikçi dizinini görüntüleme yetkiniz bulunmuyor."
                : "Tedarikçi dizinini görüntülemek için alıcı hesabınızla giriş yapın veya ücretsiz mağaza kaydı oluşturun."}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
            <Link to="/?giris=1" className="rounded-full bg-terra-600 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-terra-700" data-testid="suppliers-login">Giriş Yap</Link>
            <Link to="/?kayit=magaza" className="rounded-full border border-stone-600 px-6 py-2.5 text-sm font-semibold transition-colors hover:border-terra-500" data-testid="suppliers-register">Mağaza Kaydı Oluştur</Link>
        </div>
    </div>
);

export default function SuppliersPublic() {
    const [me, setMe] = useState(undefined);
    const [stores, setStores] = useState(null);

    useEffect(() => {
        api.get("/auth/me").then((r) => {
            setMe(r.data);
            if (["buyer", "admin"].includes(r.data.role)) {
                api.get("/public/suppliers").then((res) => setStores(res.data)).catch(() => setStores([]));
            }
        }).catch(() => setMe(null));
    }, []);
    useEffect(() => { window.scrollTo(0, 0); }, []);

    const allowed = me && ["buyer", "admin"].includes(me.role);

    return (
        <div className="min-h-screen bg-stone-950 text-stone-100" data-testid="suppliers-public-page">
            <header className="sticky top-0 z-30 border-b border-stone-800/80 bg-stone-950/85 backdrop-blur">
                <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3 sm:px-6">
                    <Link to="/" className="inline-flex items-center gap-2 rounded-full border border-stone-700 px-4 py-2 text-sm font-medium transition-colors hover:border-terra-500 hover:text-terra-400" data-testid="suppliers-back-home">
                        <ArrowLeft className="h-4 w-4" /> Anasayfaya Dön
                    </Link>
                    <span className="ml-auto text-xs uppercase tracking-[0.3em] text-terra-500">ATA Angora</span>
                </div>
            </header>

            {me === undefined ? (
                <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6"><div className="h-44 animate-pulse rounded-2xl bg-stone-900" /></div>
            ) : !allowed ? (
                <LockScreen reason={me ? "role" : "anon"} />
            ) : (
                <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
                    <h1 className="font-display text-3xl font-bold sm:text-4xl">Tedarikçiler</h1>
                    <p className="mt-2 max-w-2xl text-sm text-stone-400">Platforma kayıtlı tedarikçiler. Bu dizin yalnızca kayıtlı alıcılara açıktır.</p>

                    {!stores ? (
                        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">{[...Array(3)].map((_, i) => <div key={i} className="h-44 animate-pulse rounded-2xl bg-stone-900" />)}</div>
                    ) : !stores.length ? (
                        <div className="mt-10 rounded-3xl border border-stone-800 bg-stone-900/40 p-16 text-center" data-testid="suppliers-empty">
                            <Store className="mx-auto mb-4 h-10 w-10 text-stone-600" />
                            <p className="font-display text-xl font-bold">Tedarikçiler yakında burada</p>
                            <p className="mx-auto mt-2 max-w-md text-sm text-stone-400">Başvurusu onaylanan tedarikçiler otomatik olarak bu sayfada listelenir.</p>
                        </div>
                    ) : (
                        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {stores.map((s) => (
                                <Link key={s.supplier_id} to={`/panel/vitrin/${s.supplier_id}`} className="group block rounded-2xl border border-stone-800 bg-stone-900/40 p-5 transition-colors hover:border-terra-500/60" data-testid={`supplier-card-${s.supplier_id}`}>
                                    <div className="flex items-center gap-3">
                                        {s.logo ? (
                                            <img src={s.logo} alt={s.name} className="h-14 w-14 rounded-2xl border border-stone-700 object-cover" loading="lazy" decoding="async" />
                                        ) : (
                                            <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-stone-700 bg-stone-800 font-display text-xl font-bold text-terra-400">{(s.name || "?")[0].toUpperCase()}</div>
                                        )}
                                        <div className="min-w-0">
                                            <p className="flex items-center gap-1.5 font-display text-base font-bold leading-tight">
                                                <span className="truncate">{s.name}</span>
                                                {s.verified && <BadgeCheck className="h-4 w-4 shrink-0 text-terra-400" />}
                                            </p>
                                            {s.city && <p className="mt-0.5 flex items-center gap-1 text-xs text-stone-400"><MapPin className="h-3 w-3" /> {s.city}</p>}
                                        </div>
                                    </div>
                                    {s.description && <p className="mt-3 line-clamp-2 text-xs leading-relaxed text-stone-400">{s.description}</p>}
                                    {s.category_names?.length > 0 && (
                                        <div className="mt-3 flex flex-wrap gap-1.5">
                                            {s.category_names.slice(0, 3).map((c) => <span key={c} className="rounded-full border border-stone-700 px-2 py-0.5 text-[10px] text-stone-300">{c}</span>)}
                                            {s.category_names.length > 3 && <span className="rounded-full border border-stone-700 px-2 py-0.5 text-[10px] text-stone-400">+{s.category_names.length - 3}</span>}
                                        </div>
                                    )}
                                    <div className="mt-4 flex items-center justify-between text-xs text-stone-400">
                                        <span className="flex items-center gap-1"><Package className="h-3.5 w-3.5" /> {s.product_count} ürün</span>
                                        <span className="font-medium text-terra-400 transition-transform group-hover:translate-x-0.5">Mağazayı görüntüle →</span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
